import { useContentMotion } from "../lib/useContentMotion";
/**
 * One plugin-declared panel, rendered from its PanelSpec alone.
 *
 * The shell knows nothing about the plugin: it fetches the spec, validates it,
 * fetches each declared source, draws the blocks, and posts actions back. If
 * the spec is malformed the panel says which field and stops — it does not
 * half-render something that looks like data.
 */

import { useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api, ApiError, type PanelData, type PanelEntry, type PanelInteraction } from "../lib/api";
import { BlockView, type RunAction } from "./blocks";
import { FormAsking } from "./form";
import { declaredAudience, OWNER_AUDIENCE, type PanelAction, type PanelBlock, type PanelSpec } from "./spec";
import { SurfaceHost, PanelDeveloper, SurfaceVisibility } from "./SurfaceHost";
import { ErrorState, LoadingState } from "../shell/ui";
import { LightMotion } from "../components/LightMotion";
import { SecureInputLinks } from "../connections/SecureInputLinks";
import { settingsDestinations } from "../connections/directory";
import { savedNavigationContext, workBack, WorkContext, workLinks, workPageTabs, workspaceNavigation } from "../studio/WorkContext";
import { useWorkTool, type BackTool } from "../shell/workTools";
import { AskReport, FormDraftScope } from "./InteractionFrame";
import { readDraft, writeDraft } from "../lib/drafts";
import { chatHref, WORKSPACE_HREF } from "../connections/navigation";
import { useConversationDrawn } from "../lib/conversations";
import { useMainPanelPresence } from "./MainPanelPresence";

/**
 * What was read for one panel -- its spec, its plugin, its data, or why that
 * failed -- filed under the key it was read for.
 *
 * The spec names the page's files and reads and the key says where to ask for
 * them, so a render takes them together or not at all: what is filed under the
 * key on screen, and nothing else. They used to be separate states with the key
 * kept in a ref that an effect moved, so a render that came between the key
 * changing and that effect clearing the old page (the shell re-renders when a
 * page reports itself present) drew the old page under the new key, and its
 * frame asked for the old page's file there.
 */
interface PanelRead {
  key: string;
  spec: PanelSpec | null;
  plugin: string;
  data: PanelData | null;
  generation: string | null | undefined;
  error: unknown;
}

/** `held` with `change` written in, when `held` is `key`'s; anything of another panel's is dropped. */
function filed(held: PanelRead | null, key: string, change: Partial<Omit<PanelRead, "key">>): PanelRead {
  return { ...(held?.key === key ? held : { key, spec: null, plugin: "", data: null, generation: undefined, error: null }), ...change };
}

export function PanelView({
  panelKey,
  onToast,
  heading = "h1",
  onSubmitted,
  onAnswered,
  context,
  panels = [],
  showBackLink = false,
  onContextChange,
  onNotForOwner,
  seatKnown = true,
  presentation = "page",
  toolsInBar = false,
}: {
  panelKey: string;
  onToast: (message: string, tone: "good" | "critical") => void;
  /** `h2` when several panels share one page, so the headings still nest. */
  heading?: "h1" | "h2";
  /**
   * Told the obligation a button opened. A page that wants to show what became
   * of the submission takes this instead of the toast: "已提交" is true and
   * says nothing about whether the thing happened.
   */
  onSubmitted?: (obligationId: string, label: string) => void;
  /** Told when a request's answer was taken from this page, so its going reads as sent and not as ended elsewhere. */
  onAnswered?: () => void;
  context?: Record<string, string>;
  panels?: PanelEntry[];
  showBackLink?: boolean;
  /** Display/navigation hints only; never a tool or identity grant. */
  onContextChange?: (context: Record<string, string>) => void;
  /**
   * Told once, when the panel turns out not to be the owner's page and the
   * developer view is off. Whoever put this on screen decides what to do with
   * that: the one that is a whole route replaces the address with 今天, the
   * ones that are a region of somebody else's page let the sentence below
   * stand.
   */
  onNotForOwner?: () => void;
  /**
   * Whether the console knows who sits at this seat right now. Every read of a
   * page goes through the seat (§7.181 / §7.182), so while the seat is being
   * recovered -- the core reconnected and the console has not replayed the
   * owner yet -- the page does not ask, and asks once it is known. Eight blocks
   * each saying 「暂时无法确认当前 Owner」 was one page asking eight times inside
   * that window (r6 D900); the shell's recovery banner is the one sentence.
   */
  seatKnown?: boolean;
  /** Embedded overviews already provide their surrounding page navigation. */
  presentation?: "page" | "embedded";
  /**
   * This is the page the Agent's bar speaks for: its way back, reload, links and last change go to the bar's 更多
   * menu (`shell/workTools.tsx`) and the page keeps nothing above its content. Without a bar they stay here.
   */
  toolsInBar?: boolean;
}) {
  const developer = useContext(PanelDeveloper);
  const visible = useContext(SurfaceVisibility);
  const contentRef = useRef<HTMLDivElement>(null);
  const declared = panels.find(panel => panel.key === panelKey);
  const generation = declared?.generation_id;
  const [read, setRead] = useState<PanelRead | null>(null);
  const here = read?.key === panelKey ? read : null;
  const spec = here?.spec ?? null;
  const plugin = here?.plugin ?? "";
  const data = here?.data ?? null;
  const error = here?.error ?? null;
  const fresh = Boolean(spec && data && !error && here?.generation !== undefined
    && here.generation === data.generation_id && (!generation || here.generation === generation));
  const available = seatKnown && fresh;
  useContentMotion(contentRef, `${panelKey}:${generation ?? ""}`, visible && available && !spec?.blocks.some(block => block.kind === "surface"));
  useEffect(() => { contentRef.current?.toggleAttribute("inert", !available); }, [available]);
  const [busy, setBusy] = useState<string | null>(null);
  const [reloads, setReloads] = useState(0);
  const scope = `${panelKey}:${JSON.stringify(context ?? {})}`;
  const [savedContext, setSavedContext] = useState<{ scope: string; context: Record<string, string> } | null>(null);

  useEffect(() => {
    let live = true;
    // A different panel: nothing held belongs to it, so it goes.
    // A *reload* of the same panel keeps what is drawn until the new data
    // arrives -- unmounting the blocks would throw away what a person has
    // typed into a form that has not been submitted yet, and would flash the
    // loading feedback over a page that is already correct.
    setRead(held => held?.key !== panelKey ? null : held.error === null ? held : { ...held, error: null });
    if (!seatKnown) return;
    (async () => {
      try {
        // The server parsed it: a declaration it could not draw never gets here,
        // it answers this read with its own reason instead.
        const body = await api.panel(panelKey);
        if (!live) return;
        const fetched = await api.panelData(panelKey);
        if (!live) return;
        if (body.generation_id === undefined || body.generation_id !== fetched.generation_id) {
          throw new ApiError("界面正在更新", "填写内容已保留，请重试读取。", null, 0);
        }
        // Keep the existing form mounted. A new declaration and its data become current together.
        setRead(held => filed(held, panelKey, { spec: body.spec, plugin: body.plugin, data: fetched, generation: body.generation_id }));
      } catch (err) {
        if (live) setRead(held => filed(held, panelKey, { error: err }));
      }
    })();
    return () => {
      live = false;
    };
  }, [panelKey, generation, reloads, seatKnown]);

  /**
   * Is this page the owner's business? The panel's own declaration answers it
   * (§7.183), and until the spec is here the question has no answer yet -- so
   * nothing is refused while it loads.
   *
   * `slot` never said this and `metadata.workspace` says which rail entry a page
   * belongs under, so the shell had nothing to close an owner's address bar
   * against: every plugin panel was an owner page, including a registry dump
   * (D786 / D840). A shell-side list of which panels are which would be the
   * thing this console must not have -- a table of plugin names.
   */
  const notForOwner = spec !== null && !developer && declaredAudience(spec) !== OWNER_AUDIENCE;
  useEffect(() => {
    if (notForOwner) onNotForOwner?.();
    // `onNotForOwner` is a fresh closure on every render of the caller, so it is
    // deliberately not a dependency: what this reacts to is the answer changing.
  }, [notForOwner, panelKey]);

  const runAction: RunAction = useCallback(
    async (action: PanelAction, values: Record<string, unknown>, id: string) => {
      if (!available) return "";
      setBusy(id);
      try {
        // Values, not arguments. Which of them the tool is called with is
        // `argument_fields` / `constants` / `pack_fields_as`, and that is
        // assembled on the server -- the browser does not decide what a button
        // sends.
        const interaction = spec?.metadata?.interaction as PanelInteraction | undefined;
        const draftName = `interaction:${panelKey}:${interaction?.version}:action`;
        const payload = JSON.stringify({ action, values });
        const previous = interaction ? readDraft<{ payload: string; id: string } | null>(draftName, null) : null;
        if (previous && previous.payload !== payload) throw new Error("上一份回应尚未确认，请保留原选择重试。");
        const command = previous || { payload, id: `interaction-${crypto.randomUUID()}` };
        if (interaction) writeDraft(draftName, command);
        // The conversation the page is open in goes with the press (§7.193):
        // the entrance fills the session in, the server does not guess one.
        const session = context?.session_id || context?.session;
        const extra = {
          ...(interaction ? { interaction: { request_id: interaction.request_id, version: interaction.version, generation_id: data?.generation_id ?? null },
            command_id: command.id } : {}),
          ...(session ? { session_id: session } : {}),
        };
        const answer = Object.keys(extra).length ? await api.panelAction(panelKey, action, values, extra) : await api.panelAction(panelKey, action, values);
        if (interaction) {
          writeDraft(draftName, null);
          onAnswered?.();
        }
        if (onSubmitted && answer.obligation_id) {
          onSubmitted(answer.obligation_id, action.label);
        } else {
          onToast(interaction ? "回应已提交。" : `已提交：${action.label}`, "good");
        }
        setReloads((n) => n + 1);
        return answer.obligation_id ?? "";
      } catch (err) {
        const api_ = ApiError.fromUnknown(err);
        onToast(`${action.label}失败：${api_.detail}`, "critical");
        throw err;
      } finally {
        setBusy(null);
      }
    },
    [panelKey, onToast, onSubmitted, onAnswered, spec, data, available, context],
  );

  const reads = useMemo(() => data?.reads ?? [], [data]);
  const viewContext = savedContext?.scope === scope ? savedContext.context : context ?? {};
  // Whether the owner can read the conversation this page came from: the way back leads there only if so.
  const origin = viewContext.session_id || viewContext.session || undefined;
  const conversation = useConversationDrawn(origin);
  const navigationMeta = spec ? workspaceNavigation(spec.metadata) : null;
  const backTool: BackTool | undefined = navigationMeta ? workBack(origin, conversation)
    : showBackLink && origin ? (conversation === true ? { label: "返回原对话", href: chatHref(origin) }
      : conversation === false ? { label: "返回工作区", href: WORKSPACE_HREF } : undefined) : undefined;
  useWorkTool("back", backTool, toolsInBar && presentation === "page");
  useWorkTool("links", navigationMeta ? workLinks(navigationMeta, panelKey, viewContext) : undefined, toolsInBar && presentation === "page");
  useWorkTool("pages", navigationMeta ? workPageTabs(navigationMeta, panels, panelKey, viewContext) : undefined, toolsInBar && presentation === "page");
  // A question put to the Owner tells the bar above the composer how many questions it holds.
  const askReport = useContext(AskReport);
  const asked = Boolean(spec?.metadata?.interaction);
  const questions = spec ? spec.blocks.reduce((total, block) => total + (block.kind === "form" ? block.fields?.length ?? 0 : 0), 0) : 0;
  useEffect(() => { if (asked) askReport?.(questions || null); }, [asked, questions, askReport]);
  useMainPanelPresence(seatKnown && !error && !notForOwner && (declared || spec && data)
    ? { key: panelKey, generation_id: declared?.generation_id ?? data?.generation_id, context: viewContext } : undefined);
  if (error && !spec) return <ErrorState error={error} onRetry={() => setReloads((n) => n + 1)} />;
  /* Declared for a developer, and this is not the developer view: the page is
     not drawn at all. Said rather than left blank, because an empty stage is a
     page that looks broken -- and the sentence is the one that tells the plugin's
     author what their page is missing, the way a choice with no declared name
     says where to add one (the settings plugin's `_unnamed_choice`).
     A whole route does better than a sentence: `App.tsx` takes `onNotForOwner`
     and puts the reader back on 今天. */
  if (notForOwner) {
    return (
      <div className="panel">
        <p className="surface-read-notice" role="status">
          这一页是给开发者看的，没有声明给你看。要让它出现在这里，这个界面需要声明它的读者。
        </p>
      </div>
    );
  }
  if (!spec || !data) return <LoadingState label="正在打开界面" />;
  const surfaceOnly = spec.blocks.length === 1 && spec.blocks[0].kind === "surface";
  // A question put to the Owner: its answer button is the one marked primary, and the ways of not answering it
  // stand beside it, each with the sentence its asker wrote for it.
  const asking = Boolean(spec.metadata?.interaction);
  const askForm = spec.blocks.some(block => block.kind === "form");
  const isAnswer = (action: PanelAction) => action.metadata?.primary === true;
  const captions = asking ? escapeCaptions(spec.blocks, spec.actions ?? []) : { hints: new Map<string, string>(), taken: new Set<number>() };
  // Declared with the one that cannot answer first; read with the one that stops the asking first, as it always stood.
  const escapeButtons = asking ? (spec.actions ?? []).map((action, i) => isAnswer(action) ? null
    : <EscapeButton key={i} id={`panel:${i}`} action={action} hint={captions.hints.get(action.label)} runAction={runAction} busy={busy} />).reverse() : null;
  const navigation = workspaceNavigation(spec.metadata);
  // A page opened from a conversation the owner has no way to read (the operating loop's, a schedule's) is not sent
  // "back" to an empty one: the way out is the Agent's workspace. Until that is answered the link holds its place unseen.
  const backLink = !showBackLink || !origin ? null
    : conversation === undefined ? <a className="btn surface-return" aria-hidden="true" tabIndex={-1}>返回原对话</a>
      : conversation ? <a className="btn surface-return" href={chatHref(origin)}>返回原对话</a>
        : <a className="btn surface-return" href={WORKSPACE_HREF}>返回工作区</a>;
  const navigationBar = (reload?: ReactNode) => navigation
    ? <WorkContext navigation={navigation} panels={panels} active={panelKey} context={viewContext} tools={reload} conversation={conversation} inBar={toolsInBar} />
    : toolsInBar ? null
      : backLink || reload || surfaceOnly ? <div className="surface-tools" role="group" aria-label="界面操作">{backLink}{reload}</div> : null;
  const onStateSaved = (state: unknown) => {
    if (navigation && Object.keys(navigation.state_context).length) {
      const selected = savedNavigationContext(state, navigation.state_context, context ?? {});
      setSavedContext({ scope, context: selected });
      onContextChange?.(selected);
      return selected;
    }
  };

  return (
    <SurfaceVisibility.Provider value={visible && available}>
    <FormDraftScope.Provider value={`${panelKey}:${(spec.metadata?.interaction as PanelInteraction | undefined)?.request_id ?? ""}`}>
    {error ? <ErrorState error={error} onRetry={() => setReloads(n => n + 1)} />
      : !seatKnown ? <p role="status">当前暂不可操作，草稿已保留。</p>
        : !fresh ? <p role="status">正在更新此界面，填写内容已保留。</p> : null}
    <FormAsking.Provider value={asking && askForm ? { escapes: <div className="ask-escapes">{escapeButtons}</div> } : null}>
    <div className={`panel${surfaceOnly ? " panel-surface" : ""}${spec.metadata?.interaction ? " panel-interaction" : ""}`} ref={contentRef} aria-busy={!available}>
      {!surfaceOnly && presentation === "page" ? navigationBar() : null}
      <header className={surfaceOnly && !developer ? "panel-head-accessible" : "panel-head"}>
        {heading === "h1" ? (
          <h1 className="page-title" tabIndex={-1}>
            {spec.title}
          </h1>
        ) : (
          <h2 className="section-title">{spec.title}</h2>
        )}
        {/* The generation is an opaque id the registry mints (`gen-4`), not a
            count: the real core has no ordinal behind it. It stays in the
            tooltip, where it is useful for matching a panel to a registry row,
            and out of the sentence, where it read as "the 4th edition". */}
        {developer ? <p className="panel-origin micro" title={originTitle(data.generation_id)}>
          由插件 <code className="mono">{plugin}</code> 声明
        </p> : null}
      </header>

      <SecureInputLinks reads={reads} panelKey={panelKey} declared={spec.metadata}
        destinations={settingsDestinations(panels)}
        onInputStored={() => {
          const key = (spec.metadata?.secure_inputs as { submitted_action?: string } | undefined)?.submitted_action;
          const action = spec.actions?.find(item => item.key === key);
          if (action) void runAction(action, {}, "secure-input-submitted").catch(() => {});
        }}
        onInputFailed={() => {
          const key = (spec.metadata?.secure_inputs as { failure_action?: string } | undefined)?.failure_action;
          const action = spec.actions?.find(item => item.key === key);
          if (action) void runAction(action, {}, "secure-input-failed").catch(() => {});
        }} />

      {spec.actions?.length && !(asking && askForm) ? (
        <div className={asking ? "panel-actions ask-actions" : "panel-actions"}>
          {asking ? <div className="ask-escapes">{escapeButtons}</div> : null}
          {spec.actions.map((action, i) => asking && !isAnswer(action) ? null : (
            <BlockActionButton
              key={i}
              id={`panel:${i}`}
              action={action}
              runAction={runAction}
              busy={busy}
            />
          ))}
        </div>
      ) : null}

      {/* The one hook §7.130 adds to the shell: six kinds are drawn from the
          declaration, and the seventh is hosted. It is dispatched here rather
          than in `blocks.tsx`'s renderer table because a surface takes none of
          a block renderer's inputs — it reads nothing out of `reads` and posts
          nothing through `runAction`; its own bridge is its door. */}
      {spec.blocks.map((block, i) =>
        asking && captions.taken.has(i) ? null : block.kind === "surface" ? (
          <SurfaceHost key={`${i}:${JSON.stringify(context ?? {})}`} panelKey={panelKey} block={block} index={i} onToast={onToast} context={context} onStateSaved={onStateSaved}
            pageTitle={declared?.title || spec.title} viewContext={viewContext}
            presentation={presentation} renderToolbar={surfaceOnly ? navigationBar : undefined} toolsInBar={toolsInBar} />
        ) : (
          <BlockView
            key={i}
            block={block}
            index={i}
            reads={reads}
            runAction={runAction}
            busy={busy}
          />
        ),
      )}
    </div>
    </FormAsking.Provider>
    </FormDraftScope.Provider>
    </SurfaceVisibility.Provider>
  );
}

/**
 * What each way of not answering a question does, in the words its asker wrote for the caption under the boxes
 * (「- 不用问了：…」, one line a way). A caption whose every line is one of them moves onto the buttons, and is
 * not drawn twice; any other caption stays where it is.
 */
function escapeCaptions(blocks: PanelBlock[], actions: PanelAction[]) {
  const hints = new Map<string, string>();
  const taken = new Set<number>();
  blocks.forEach((block, index) => {
    if (block.kind !== "markdown" || !block.body) return;
    const lines = block.body.split(/\r?\n/).map(line => line.trim()).filter(Boolean);
    const found = lines.map(line => {
      const parts = /^[-*]\s*(.+?)[：:]\s*(.+)$/.exec(line);
      return parts && actions.some(action => action.label === parts[1]) ? parts : null;
    });
    if (!found.length || !found.every(Boolean)) return;
    for (const parts of found) hints.set(parts![1], parts![2]);
    taken.add(index);
  });
  return { hints, taken };
}

/** A way of not answering: quiet, and its asker's sentence one line long, whole in the tooltip. */
function EscapeButton({ action, hint, runAction, busy, id }: {
  action: PanelAction; hint?: string; runAction: RunAction; busy: string | null; id: string;
}) {
  return (
    <button
      type="button"
      className="btn btn-quiet ask-escape"
      aria-label={action.label}
      title={hint ? `${action.label}：${hint}` : undefined}
      disabled={busy === id}
      aria-busy={busy === id}
      onClick={() => void runAction(action, {}, id).catch(() => {})}
    >
      {busy === id ? <><LightMotion compact state="working" />处理中</> : <>
        <span className="ask-escape-label">{action.label}</span>
        {hint ? <span className="ask-escape-hint" aria-hidden="true">{hint}</span> : null}
      </>}
    </button>
  );
}

/** The registration this panel was drawn from, for the origin line's tooltip. */
function originTitle(generationId: string | null): string | undefined {
  return generationId ? `注册代次 ${generationId}` : undefined;
}

function BlockActionButton({
  action,
  runAction,
  busy,
  id,
}: {
  action: PanelAction;
  runAction: RunAction;
  busy: string | null;
  id: string;
}) {
  return (
    <button
      type="button"
      className={action.metadata?.primary === true ? "btn btn-accent" : "btn"}
      disabled={busy === id}
      aria-busy={busy === id}
      onClick={() => void runAction(action, {}, id).catch(() => {})}
    >
      {busy === id ? <><LightMotion compact state="working" />处理中</> : action.label}
    </button>
  );
}
